import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Modal,
  Paper,
  CircularProgress,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import CloseIcon from "@mui/icons-material/Close";
import DocumentPreview from "../Common/ComponentDocumentPreview";
import { translateText } from "../../translations/translationService";
import { useSelector } from "react-redux";
import { BACKGROUND_COLORS, BORDER_COLORS, TEXT_COLORS, BRAND_COLORS } from "../../style/colors.js";

export default function MenuDocument({ data, openModal: externalOpenModal = false, onModalStateChange }) {
  console.log("data", data);
  console.log("externalOpenModal", externalOpenModal);
  console.log("onModalStateChange", onModalStateChange);

  const [modalVisible, setModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const lang = useSelector((state) => state.lang.value);

  // Memoizar los documentos procesados y filtrados
  const { documents, filteredDocuments } = useMemo(() => {
    const nameLinksString = data?.documentacion;

    if (!nameLinksString) {
      return { documents: [], filteredDocuments: [] };
    }

    const documentsArray = nameLinksString
      .split(",,,")
      .map((doc) => doc.trim())
      .filter(Boolean); // Eliminar strings vacíos

    // Filtrar documentos basado en criterios
    const referencia = data.referencia;
    const subReferencia = data.sureferencia;

    if (!referencia) {
      return { documents: documentsArray, filteredDocuments: documentsArray };
    }

    const matchedDocuments = documentsArray.filter((doc) => {
      const initialChar = doc.match(/[A-Za-z]/)?.[0];
      return doc.includes(subReferencia) || initialChar === "T";
    });

    return { documents: documentsArray, filteredDocuments: matchedDocuments };
  }, [data?.documentacion, data?.referencia, data?.sureferencia]);

  // Simular carga asíncrona
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100); // Simular tiempo de procesamiento mínimo

    return () => clearTimeout(timer);
  }, []);

  // Callbacks memoizados
  const handleOpenModal = useCallback(() => {
    setModalVisible(true);
    if (onModalStateChange) onModalStateChange(true);
  }, [onModalStateChange]);
  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    if (onModalStateChange) onModalStateChange(false);
  }, [onModalStateChange]);

  // Efecto para abrir el modal desde fuera
  useEffect(() => {
    if (externalOpenModal) {
      setModalVisible(true);
      if (onModalStateChange) onModalStateChange(true);
    }
  }, [externalOpenModal, onModalStateChange]);

  // Efecto para abrir el modal desde fuera
  useEffect(() => {
    if (externalOpenModal) {
      setModalVisible(true);
      if (onModalStateChange) onModalStateChange(true);
    }
  }, [externalOpenModal, onModalStateChange]);

  const handleOpenLink = useCallback((document) => {
    window.open(document, "_blank");
  }, []);

  // Textos traducidos memoizados
  const translatedTexts = useMemo(
    () => ({
      documents: translateText("Documentos", lang),
      noDocuments: translateText(
        "No se encontraron documentos que coincidan con los criterios.",
        lang
      ),
      close: translateText("Cerrar", lang),
    }),
    [lang]
  );

  // Estilos memoizados mejorados
  const modalStyles = useMemo(
    () => ({
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: { xs: "95%", sm: "85%", md: "70%", lg: "60%" },
      bgcolor: BACKGROUND_COLORS.CARD,
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
      p: 0,
      borderRadius: 3,
      maxHeight: "85vh",
      overflow: "hidden",
      border: `1px solid ${BORDER_COLORS.PRIMARY}`,
    }),
    []
  );

  const headerStyles = useMemo(
    () => ({
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      p: 3,
      borderBottom: `1px solid ${BORDER_COLORS.PRIMARY}`,
      bgcolor: BACKGROUND_COLORS.SECONDARY,
      borderRadius: "12px 12px 0 0",
    }),
    []
  );

  const contentStyles = useMemo(
    () => ({
      p: 2,
      maxHeight: "60vh",
      overflow: "auto",
      bgcolor: BACKGROUND_COLORS.CARD,
      "&::-webkit-scrollbar": {
        width: "6px",
      },
      "&::-webkit-scrollbar-track": {
        background: BACKGROUND_COLORS.SECONDARY,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-thumb": {
        background: BORDER_COLORS.MEDIUM,
        borderRadius: "3px",
        "&:hover": {
          background: BORDER_COLORS.LIGHT,
        },
      },
    }),
    []
  );

  const documentItemStyles = useMemo(
    () => ({
      display: "flex",
      alignItems: "center",
      py: 0.75,
      px: 1,
      mb: 0.5,
      borderRadius: 2,
      cursor: "pointer",
      transition: "all 0.2s ease-in-out",
      border: "1px solid transparent",
      "&:hover": {
        backgroundColor: BACKGROUND_COLORS.PRIMARY,
        borderColor: BORDER_COLORS.PRIMARY,
        transform: "translateY(-1px)",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
      },
      "&:active": {
        transform: "translateY(0px)",
      },
    }),
    []
  );

  const footerStyles = useMemo(
    () => ({
      p: 3,
      borderTop: `1px solid ${BORDER_COLORS.PRIMARY}`,
      bgcolor: BACKGROUND_COLORS.SECONDARY,
      borderRadius: "0 0 12px 12px",
    }),
    []
  );

  const closeButtonStyles = useMemo(
    () => ({
      backgroundColor: BRAND_COLORS.PRIMARY,
      color: TEXT_COLORS.LIGHT,
      px: 4,
      py: 1.5,
      borderRadius: 2,
      minWidth: "120px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
      transition: "all 0.2s ease-in-out",
      "&:hover": {
        backgroundColor: BRAND_COLORS.PRIMARY_LIGHT,
        transform: "translateY(-1px)",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
      },
      "&:active": {
        transform: "translateY(0px)",
      },
    }),
    []
  );

  const iconButtonStyles = useMemo(
    () => ({
      color: BRAND_COLORS.PRIMARY,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      transition: "all 0.2s ease-in-out",
      borderRadius: 2,
      p: 1.5,
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.04)",
        transform: "scale(1.05)",
      },
      "&:active": {
        transform: "scale(0.98)",
      },
    }),
    []
  );

  const renderDocuments = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            py: 6,
          }}>
          <CircularProgress color='primary' size={40} thickness={4} />
          <Typography
            variant='body2'
            sx={{ mt: 2, color: "#64748b", fontWeight: 500 }}>
            {translateText("Cargando documentos...", lang)}
          </Typography>
        </Box>
      );
    }

    if (filteredDocuments.length === 0) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            py: 6,
            textAlign: "center",
          }}>
          <FolderIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 2 }} />
          <Typography
            variant='h6'
            sx={{ color: "#475569", fontWeight: 500, mb: 1 }}>
            {translateText("No hay documentos", lang)}
          </Typography>
          <Typography
            variant='body2'
            sx={{ color: "#64748b", maxWidth: "300px" }}>
            {translatedTexts.noDocuments}
          </Typography>
        </Box>
      );
    }

    return (
      <Box>
        <Typography
          variant='body2'
          sx={{
            color: "#64748b",
            mb: 2,
            fontWeight: 500,
            pl: 1,
          }}>
          {filteredDocuments.length} {filteredDocuments.length === 1 
            ? translateText("documento encontrado", lang)
            : translateText("documentos encontrados", lang)}
        </Typography>
        {filteredDocuments.map((doc, index) => (
          <Box
            key={`${doc}-${index}`}
            sx={documentItemStyles}>
            <DocumentPreview document={doc} data={data} />
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box>
      <IconButton
        onClick={handleOpenModal}
        sx={iconButtonStyles}
        aria-label='Abrir documentos'
        disabled={externalOpenModal}>
        <FolderIcon fontSize='large' />
      </IconButton>

      <Modal
        open={modalVisible}
        onClose={handleCloseModal}
        aria-labelledby='modal-title'
        aria-describedby='modal-description'
        closeAfterTransition>
        <Paper sx={modalStyles}>
          {/* Header del modal mejorado */}
          <Box sx={headerStyles}>
            <Box>
              <Typography
                id='modal-title'
                variant='h5'
                component='h2'
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  mb: 0.5,
                }}>
                {translatedTexts.documents}
              </Typography>
              <Typography variant='body2' sx={{ color: "#64748b" }}>
                {translateText("Selecciona un documento para visualizar", lang)}
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseModal}
              aria-label='Cerrar modal'
              sx={{
                color: "#64748b",
                "&:hover": {
                  backgroundColor: BACKGROUND_COLORS.SECONDARY,
                  color: "#374151",
                },
              }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Contenido principal mejorado */}
          <Box component='main' id='modal-description' sx={contentStyles}>
            {renderDocuments()}
          </Box>

          {/* Footer del modal mejorado */}
          <Box sx={footerStyles}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <IconButton
                color='secondary'
                onClick={handleCloseModal}
                sx={closeButtonStyles}>
                <Typography
                  variant='button'
                  sx={{
                    color: "white",
                    fontWeight: 600,
                    letterSpacing: "0.02em",
                  }}>
                  {translatedTexts.close}
                </Typography>
              </IconButton>
            </Box>
          </Box>
        </Paper>
      </Modal>
    </Box>
  );
}
